import React from 'react';
import { Common } from '../../utils/common';

/**
 * @typedef {Object} TagGroup
 * @property {number} tag_group_id
 * @property {number} auto_group_id
 * @property {string} name
 * @property {number} ordinal
 * @property {boolean} is_used
 */

/**
 * @param {Object} props
 * @param {Function} props.hideManageTagGroups
 * @param {string} props.groupId
 * @param {Function} props.reloadTagsTable
 */
export function ManageTagGroupsComponent({ hideManageTagGroups, groupId, reloadTagsTable }) {
  const [tagGroupData, setTagGroupData] = React.useState(/**@type {TagGroup[]} */ ([]));
  const [emptyNamesError, setEmptyNamesError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    fetch(`/admin/groups/${groupId}/tag_groups`)
      .then((response) => response.json())
      .then(({ data }) => {
        setTagGroupData(data);
      });
  }, [groupId]);

  const sortedTagGroups = tagGroupData.sort((a, b) => a.ordinal - b.ordinal);

  /**
   * @param {number} tagGroupId
   * @param {string} value
   */
  function handleNameChange(tagGroupId, value) {
    const updatedData = tagGroupData.map((item) => {
      if (item.tag_group_id === tagGroupId) {
        return { ...item, name: value };
      } else {
        return item;
      }
    });
    setTagGroupData(updatedData);
  }

  /**
   * @param {number} tagGroupId
   * @param {'up' | 'down'} direction
   */
  function handleReorder(tagGroupId, direction) {
    const currentIndex = sortedTagGroups.findIndex((item) => item.tag_group_id === tagGroupId);
    const currentOrdinal = sortedTagGroups[currentIndex].ordinal;

    const updatedData = [...sortedTagGroups];

    if (direction === 'up' && currentIndex > 0) {
      updatedData[currentIndex].ordinal = updatedData[currentIndex - 1].ordinal;
      updatedData[currentIndex - 1].ordinal = currentOrdinal;
      setTagGroupData(updatedData);
    } else if (direction === 'down' && currentIndex < sortedTagGroups.length - 1) {
      updatedData[currentIndex].ordinal = updatedData[currentIndex + 1].ordinal;
      updatedData[currentIndex + 1].ordinal = currentOrdinal;
      setTagGroupData(updatedData);
    }
  }

  function addTagGroup() {
    let ordinal = 1;
    const lastTagGroup = sortedTagGroups.at(-1);
    if (lastTagGroup) {
      ordinal = lastTagGroup.ordinal + 1;
    }

    // Need to generate a random id for the new tag group to be used as a key.
    // When saving, this temporary id will be removed.
    const tag_group_id = Math.random();

    const newTagGroup = {
      name: '',
      ordinal,
      tag_group_id,
      is_used: false,
      auto_group_id: Number(groupId),
    };
    setTagGroupData([...tagGroupData, newTagGroup]);
  }

  async function handleSave() {
    setEmptyNamesError(false);
    if (tagGroupData.some((item) => item.name.trim() === '')) {
      setEmptyNamesError(true);
      return;
    }

    // Remove the temporary tag_group_id that was used for the new tag group.
    const updatedData = tagGroupData.map((item) => {
      // If the tag_group_id is less than 1, it is a temporary id generated with Math.random()
      if (item.tag_group_id < 1) {
        // Just return the name and ordinal
        return { name: item.name, ordinal: item.ordinal };
      } else {
        return item;
      }
    });

    try {
      setIsLoading(true);

      const response = await fetch(`/admin/groups/${groupId}/tag_groups/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
        },
        body: JSON.stringify({ data: updatedData }),
      });
      const json = await response.json();
      if (response.ok) {
        setTagGroupData(json.data);

        Common.notify('Tag groups updated successfully.', 'success');

        let tagGroupsDataBinder = new window.DataBinder(
          `/admin/groups/${groupId}/tag_groups`,
          true
        );
        tagGroupsDataBinder.fetchData();
        reloadTagsTable();
      } else {
        throw new Error(json.error);
      }
    } catch (error) {
      Common.notify(`Failed to update tag groups: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * @param {number} id
   */
  function handleRemove(id) {
    const updatedData = tagGroupData.filter((item) => item.tag_group_id !== id);
    setTagGroupData(updatedData);
  }

  function handleCancel() {
    hideManageTagGroups();
  }

  return (
    <div style={{ paddingLeft: 20, paddingRight: 20 }}>
      <h3>
        <i
          role='button'
          onClick={handleCancel}
          className='fa fa-chevron-left'
          style={{ marginRight: 12 }}
        />
        Manage Tag Groups
      </h3>
      <p>You can create new tag groups, and rename, reorder, or delete existing tag groups.</p>
      <button onClick={addTagGroup} className='btn btn-primary pull-right'>
        Add Tag Group
      </button>

      {emptyNamesError ? (
        <div className='alert alert-danger' style={{ marginTop: 20 }}>
          Tag group names cannot be empty.
        </div>
      ) : null}

      <table className='table' style={{ width: '100%', marginTop: 20 }}>
        <thead>
          <tr>
            <th className='col-xs-1'></th>
            <th className='col-xs-1'></th>
            <th className='col-xs-5'>Tag Group Name</th>
            <th className='col-xs-3 text-center'>Active</th>
            <th className='col-xs-2'></th>
          </tr>
        </thead>
        <tbody>
          {sortedTagGroups.map((tag, index) => (
            <tr key={tag.tag_group_id}>
              <td className='table_cell_centered_text'>
                <div style={{ display: 'inline-flex', columnGap: 6 }}>
                  <div style={{ height: 20, width: 20 }}>
                    {index > 0 && (
                      <span
                        role='button'
                        style={{ fontSize: 20 }}
                        className='text-primary fa fa-chevron-up'
                        onClick={() => handleReorder(tag.tag_group_id, 'up')}
                      ></span>
                    )}
                  </div>
                  <div style={{ height: 20, width: 20 }}>
                    {index < tagGroupData.length - 1 && (
                      <span
                        role='button'
                        style={{ fontSize: 20 }}
                        className='text-primary fa fa-chevron-down'
                        onClick={() => handleReorder(tag.tag_group_id, 'down')}
                      ></span>
                    )}
                  </div>
                </div>
              </td>
              <td className='table_cell_centered_text' style={{ fontSize: 14, fontWeight: 'bold' }}>
                {index + 1}
              </td>
              <td>
                <input
                  className='form-control'
                  type='text'
                  value={tag.name}
                  onChange={(e) => handleNameChange(tag.tag_group_id, e.target.value)}
                />
              </td>
              <td className='table_cell_centered_text'>
                <i
                  className={
                    tag.is_used ? 'glyphiconyes glyphicon-ok' : 'glyphiconno glyphicon-remove'
                  }
                />
              </td>
              <td className='table_cell_centered_text'>
                {tag.is_used ? null : (
                  <button
                    onClick={() => handleRemove(tag.tag_group_id)}
                    className='btn btn-link text-danger'
                  >
                    Remove
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', justifyContent: 'flex-end', columnGap: 6 }}>
        <button className='btn btn-default' onClick={handleCancel}>
          Back
        </button>
        <button
          disabled={tagGroupData.length === 0 || isLoading}
          className='btn btn-primary'
          onClick={handleSave}
        >
          Save
        </button>
      </div>
    </div>
  );
}
